export type AlertSeverity = 'error' | 'warning' | 'info'

export type ConfigAlert = {
  slug: string
  severity: AlertSeverity
  message: string
  dismissible: boolean
}

export type GetConfigAlertsResponse = {
  alerts: ConfigAlert[]
}

export type AlertTab = 'requirements' | 'ontology' | 'pipeline' | 'training'

export type AlertMapping = {
  tab: AlertTab
  anchor: string
}

export const ANCHOR_LABELS: Record<string, string> = {
  capacityRequirements: 'Capacity Requirements',
  resolutionTargets: 'Resolution Targets',
  benchmarkSettings: 'Benchmark Settings',
  programInformation: 'Program Information',
  auditQueue: 'Resolution Audits & Benchmarking Settings',
  features: 'Features',
  predictions: 'Predictions',
  instructions: 'Instructions',
  qualityControl: 'Quality Control',
  settings: 'Settings',
  modules: 'Modules',
}

export const ALERT_MAPPING: Record<string, AlertMapping> = {
  // Requirements Tab
  no_availability_windows: { tab: 'requirements', anchor: 'capacityRequirements' },
  overlapping_availability_windows: { tab: 'requirements', anchor: 'capacityRequirements' },
  unlimited_bandwidth: { tab: 'requirements', anchor: 'capacityRequirements' },
  zero_system_concurrency: { tab: 'requirements', anchor: 'capacityRequirements' },
  zero_program_concurrency: { tab: 'requirements', anchor: 'capacityRequirements' },
  low_volume_limits: { tab: 'requirements', anchor: 'capacityRequirements' },
  low_concurrent_limit: { tab: 'requirements', anchor: 'capacityRequirements' },
  no_target_resolution_time: { tab: 'requirements', anchor: 'resolutionTargets' },
  inactivity_exceeds_target: { tab: 'requirements', anchor: 'resolutionTargets' },
  low_priority_timeout_too_short: { tab: 'requirements', anchor: 'resolutionTargets' },
  instance_excluded_from_metrics: { tab: 'requirements', anchor: 'auditQueue' },

  // Ontology Tab
  no_ontology_features: { tab: 'ontology', anchor: 'features' },
  prediction_expects_missing_classes: { tab: 'ontology', anchor: 'predictions' },
  required_classification_no_options: { tab: 'ontology', anchor: 'features' },

  // Pipeline Tab
  no_task_instructions: { tab: 'pipeline', anchor: 'instructions' },
  consensus_disabled: { tab: 'pipeline', anchor: 'qualityControl' },
  missing_ontology_reference: { tab: 'pipeline', anchor: 'instructions' },
  media_required_but_zero_expected: { tab: 'pipeline', anchor: 'settings' },

  // Training Tab
  no_published_training: { tab: 'training', anchor: 'modules' },
  requalification_disabled: { tab: 'training', anchor: 'modules' },
}

const CRITICAL_ERRORS_UNDEFINED_ALERT_SLUG_PREFIX = 'critical_errors_undefined_'
const CRITICAL_ERRORS_UNDEFINED_ALERT_MAPPING: AlertMapping = {
  tab: 'ontology',
  anchor: 'predictions',
}

export function getAlertMapping(slug: string): AlertMapping | undefined {
  if (slug.startsWith(CRITICAL_ERRORS_UNDEFINED_ALERT_SLUG_PREFIX)) {
    return CRITICAL_ERRORS_UNDEFINED_ALERT_MAPPING
  }

  return ALERT_MAPPING[slug]
}
